import React, { Suspense, useCallback, useEffect } from 'react';
import router from './routes/routes';
import './App.css';
import './styles/emoji.css';
import './styles/tiptap.css';
import './styles/embeds.css';
import './styles/inlineRte.css';
import './styles/highCharts.css';
import './styles/desktop.scss';
import './styles/mobile.scss';
import PrivateRoute from './component/privateRoutes';
import { Route, Router, Switch } from 'react-router-dom';
import history from './history';
import { APP_INIT_RESPONSE_TYPE, COLOR_THEME, COLOR_THEME_TYPE } from './global/constants';
import { appInit, applyCustomBranding, applyDefaultBranding, getBrandDataBasedOnHostname } from './services/appService';
import {
  getColorWithOpacity,
  getIsDarkMode,
  isAndroid,
  isEmpty,
  isIOS,
  isIpad,
  isIphone,
  isMAC,
  isMediaRecordingSupported,
  isReactNative,
  isWindows,
  isElectronApp
} from './helpers/common';
import { captureException } from './services/logService';
import UserPreferenceSingleton from './helpers/userPreferenceSingleton';
import {
  deleteDocumentFromCollection,
  loadFirebaseTimerData,
  subscribeToDocument,
  subscribeToWorkspaceProjects,
  subscribeToWorkspaceViews
} from './helpers/firebaseHelper';
import AppLoading from './component/loading/appLoading';
import useRudderStackAnalytics from './useRudderAnalytics';
import { APP_ANALYTICS } from './global/analyticsConstants';
import TabsBar from './component/tabsBar';
import ReactScanMonitoring from './component/reactScanMonitoring';
import { rootStore } from './mobx/rootStore';
import { NewTaskFilterInterface } from './interfaces/TaskInterface';
import { ProjectPreferenceData } from './mobx/stores/projectStore';
import { observer } from 'mobx-react-lite';
import { AppMain } from './styles/AppStyles';
import BottomNavigationWrapper from './component/bottomNavigation/bottomNavigationWrapper';
import TagManager from 'react-gtm-module';
import { MessageSyncSignal, syncMessagesAndGroupsInBackground } from './services/chatMessageSyncService';
import debounce from 'lodash/debounce';

declare global {
  interface Window {
    electronApi?: {
      setHistory?: (history: typeof import('./history').default) => void;
      changeTheme: (theme: string) => void;
      subscribeFcm?: () => void;
      windowAction?: (action: 'minimize' | 'maximize' | 'fullscreen' | 'close') => void;
    };
    Intercom?: (command: string) => void;
    ReactNativeWebView?: {
      postMessage?: (message: string) => void;
    };
  }
}

const MediaRecorderProvider = isMediaRecordingSupported() ? React.lazy(() => import('./hooks/useMediaRecorder')) : null;

function App() {
  const analytics = useRudderStackAnalytics();
  const { currentUser, workspaceStore, appStore } = rootStore;
  const { themeType, themeMode } = appStore;
  const { currentWorkspace } = workspaceStore;
  const { customBranding } = appStore;

  const loadThemeData = useCallback(() => {
    const theme = UserPreferenceSingleton.getInstance().getThemeMode();
    if (!theme || typeof theme !== 'number') {
      appStore.setThemeType(COLOR_THEME.LIGHT);
    } else {
      appStore.setThemeType(theme);
    }
  }, [appStore]);

  const addClassAndStyleBasedOnTheme = useCallback(
    (theme: string) => {
      const body = document.body;
      if (theme === 'dark') {
        body.classList.add('dark');
        body.classList.remove('light');
        if (customBranding?.primaryDarkColor) {
          const hoverColor = getColorWithOpacity(customBranding?.primaryDarkColor);
          body.style.setProperty('--brand-primary', customBranding?.primaryDarkColor);
          body.style.setProperty('--brand-primary-hover', hoverColor ? hoverColor : customBranding?.primaryDarkColor);
        } else {
          body.style.setProperty('--brand-primary', '#14151A');
          body.style.setProperty('--brand-primary-hover', '#14151AE6');
        }
      } else {
        body.classList.add('light');
        body.classList.remove('dark');
        if (customBranding?.primaryColor) {
          const hoverColor = getColorWithOpacity(customBranding?.primaryColor);
          body.style.setProperty('--brand-primary', customBranding?.primaryColor);
          body.style.setProperty('--brand-primary-hover', hoverColor ? hoverColor : customBranding?.primaryColor);
        } else {
          body.style.setProperty('--brand-primary', '#14151A');
          body.style.setProperty('--brand-primary-hover', '#14151AE6');
        }
      }
    },
    [customBranding?.primaryColor, customBranding?.primaryDarkColor]
  );

  const loadBrandingDetails = useCallback(() => {
    try {
      const mobxWorkspaceId = rootStore.workspaceStore.currentWorkspace?.id;
      const localWorkspaceId = UserPreferenceSingleton.getInstance().getWorkspace()?.id;
      const workspaceId = mobxWorkspaceId || localWorkspaceId;

      if (workspaceId) {
        const currentDesktopView = UserPreferenceSingleton.getInstance().getExtractedDesktopView();
        rootStore.appStore.setExtractedDesktopView(typeof currentDesktopView === 'number' ? currentDesktopView : 0);
        const localBrandingData = UserPreferenceSingleton.getInstance().getBranding(workspaceId);
        if (localBrandingData && Object.keys(localBrandingData).length > 0) {
          applyCustomBranding(localBrandingData);
        } else {
          applyDefaultBranding();
        }
      } else if (!workspaceId) {
        getBrandDataBasedOnHostname();
      }
    } catch (e) {
      captureException(e);
    }
  }, []);

  // 1. Initialization Logic (Analytics, GTM, Branding, App Init)
  useEffect(() => {
    if (analytics) {
      analytics.track(APP_ANALYTICS.APP_LOADED, {
        platform: isElectronApp() ? 'electron' : isReactNative() ? 'reactnative' : 'web'
      });
    }

    if (typeof window !== 'undefined') {
      TagManager.initialize({ gtmId: 'GTM-N8SMZXPX' });
    }

    loadBrandingDetails();
    loadThemeData();
  }, [analytics, loadBrandingDetails, loadThemeData]);

  useEffect(() => {
    async function init() {
      try {
        const result = await appInit(true);
        if (result && result?.type === APP_INIT_RESPONSE_TYPE.REDIRECT && result.path) {
          history.push(result.path);
        }
      } catch (e) {
        captureException(e);
      }
    }
    init();
  }, []);

  useEffect(() => {
    if (window.electronApi) {
      window.electronApi.setHistory?.(history);
    }

    if (isReactNative()) {
      window.addEventListener(
        'message',
        (message) => {
          let dataPayload;
          try {
            dataPayload = JSON.parse(message.data);
          } catch {
            return;
          }

          if (!dataPayload) return;

          const { type, data } = dataPayload;

          if (type === 'redirect') {
            const url = new URL(data);
            history.push(url.pathname + url.search);
          }
        },
        { capture: true }
      );
    }

    // Access the body tag
    const body = document.body;
    if (isElectronApp()) {
      body.classList.add('electron');

      if (isWindows()) {
        body.classList.add('windows');
      }

      if (isMAC()) {
        body.classList.add('mac');
      }
    }

    if (isReactNative()) {
      body.classList.add('react-native');

      if (isIOS()) {
        body.classList.add('ios');
      }

      if (isIphone()) {
        body.classList.add('iphone');
      }

      if (isIpad()) {
        body.classList.add('ipad');
      }

      if (isAndroid()) {
        body.classList.add('android');
      }
    }

    // Apply class based on theme
    addClassAndStyleBasedOnTheme(themeMode?.theme);

    if (window.electronApi && themeType) {
      if (themeType === COLOR_THEME.DARK) {
        window.electronApi.changeTheme?.('dark');
      } else if (themeType === COLOR_THEME.SYSTEM_DEFAULT) {
        window.electronApi.changeTheme?.('system');
      } else {
        window.electronApi.changeTheme?.('light');
      }
    }

    // Intercom initialization handled elsewhere

    if (isReactNative()) {
      if (themeType === COLOR_THEME.DARK) {
        window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: 'update-theme', data: 'dark' }));
      } else if (themeType === COLOR_THEME.SYSTEM_DEFAULT) {
        window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: 'update-theme', data: 'system' }));
      } else {
        window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: 'update-theme', data: 'light' }));
      }
    }
  }, [addClassAndStyleBasedOnTheme, themeMode.theme, themeType]);

  // 3. Data & Firebase Sync Logic
  useEffect(() => {
    let unsubscribeTimer: (() => void) | undefined;
    if (!isEmpty(currentWorkspace?.id)) {
      unsubscribeTimer = loadFirebaseTimerData();
    }

    if (!currentUser?.id || !currentWorkspace?.id) {
      return () => {
        unsubscribeTimer?.();
      };
    }

    const unsubscribeProjects = subscribeToWorkspaceProjects(currentUser.id, currentWorkspace.id, (snapshot) => {
      // Process only the changed documents from the snapshot instead of re-reading the
      // entire projects-preferences collection. This breaks the listener-to-read cascade
      // where one document change would trigger a full getDocs() of every preference doc.
      const docChanges = snapshot.docChanges();
      if (docChanges.length <= 0) return;

      const changedProjectsData: Record<string, ProjectPreferenceData> = {};
      docChanges.forEach((change) => {
        if (change.type === 'added' || change.type === 'modified') {
          changedProjectsData[change.doc.id] = change.doc.data() as ProjectPreferenceData;
        }
      });

      if (Object.keys(changedProjectsData).length === 0) return;
      rootStore.projectStore.updateWithFirebase(changedProjectsData);
    });

    const unsubscribeViews = subscribeToWorkspaceViews(currentUser.id, currentWorkspace.id, (snapshot) => {
      // Apply the preference data straight from the snapshot diff instead of issuing a
      // getDoc() per view. This breaks the listener-to-read cascade where a single
      // preference change would trigger a Firestore read for every view.
      const docChanges = snapshot.docChanges();
      if (docChanges.length <= 0) return;

      docChanges.forEach((docChange) => {
        if (docChange.type !== 'modified') return;

        const view = rootStore.viewStore.views.find((v) => v.id === docChange.doc.id);
        if (view) view.updateViewPreferenceFromFirebase(docChange.doc.data() as Partial<NewTaskFilterInterface>);
      });
    });

    return () => {
      unsubscribeTimer?.();
      unsubscribeProjects();
      unsubscribeViews();
    };
  }, [currentUser?.id, currentWorkspace?.id]);

  useEffect(() => {
    // Re-runs when the user or workspace becomes ready; starting before both are loaded would
    // otherwise skip the listener for the whole session.
    const userId = currentUser?.id;
    if (userId && currentWorkspace?.id) {
      const uniqueId = `${currentWorkspace?.id}_${userId}`;

      // Debounce the sync so a burst of snapshot updates collapses into a single sync run
      // instead of firing the two API calls + Firestore delete on every snapshot. The
      // trigger document is removed only after the sync resolves successfully, so a failed
      // sync leaves the signal in place to be retried by the next snapshot.
      // Latest snapshot of the trigger doc; it accumulates the changed conversation ids until deleted.
      let latestSignal: MessageSyncSignal | null = null;
      const debouncedSync = debounce(async () => {
        const didSync = await syncMessagesAndGroupsInBackground(latestSignal);
        if (didSync) {
          deleteDocumentFromCollection('messages', uniqueId);
        }
      }, 500);

      const unsubscribe = subscribeToDocument('messages', uniqueId, (data) => {
        if (data) {
          latestSignal = data;
          debouncedSync();
        }
      });
      return () => {
        debouncedSync.cancel();
        unsubscribe?.();
      };
    }
  }, [currentUser?.id, currentWorkspace?.id]);

  // 4. System Color Scheme Listener
  useEffect(() => {
    const isDarkMode: boolean = getIsDarkMode();
    const colorSchemeListener = (e: MediaQueryListEvent) => {
      if (themeType === COLOR_THEME.SYSTEM_DEFAULT) {
        appStore.setThemeMode(e.matches ? COLOR_THEME_TYPE.DARK : COLOR_THEME_TYPE.LIGHT);
      }
    };

    let colorSchemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    colorSchemeMediaQuery.addEventListener('change', colorSchemeListener);

    if (themeType === COLOR_THEME.SYSTEM_DEFAULT) {
      appStore.setThemeMode(isDarkMode ? COLOR_THEME_TYPE.DARK : COLOR_THEME_TYPE.LIGHT);
    } else {
      appStore.setThemeMode(themeType === COLOR_THEME.DARK ? COLOR_THEME_TYPE.DARK : COLOR_THEME_TYPE.LIGHT);
    }

    return () => {
      colorSchemeMediaQuery?.removeEventListener('change', colorSchemeListener);
    };
  }, [appStore, themeType]);

  const routeComponents = router?.map((item, i) =>
    item?.private ? <PrivateRoute exact key={i} {...item} /> : <Route exact key={i} {...item} />
  );

  return (
    <>
      <div className='drag-header'></div>
      <AppMain>
        {MediaRecorderProvider ? (
          <Suspense
            fallback={
              <div>
                <AppLoading />
              </div>
            }>
            <MediaRecorderProvider>
              <Router history={history}>
                <ReactScanMonitoring />
                <TabsBar />
                <BottomNavigationWrapper />
                <Switch>{routeComponents}</Switch>
              </Router>
            </MediaRecorderProvider>
          </Suspense>
        ) : (
          <Router history={history}>
            <ReactScanMonitoring />
            <TabsBar />
            <BottomNavigationWrapper />
            <Switch>{routeComponents}</Switch>
          </Router>
        )}
      </AppMain>
    </>
  );
}

export default observer(App);
